import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const SORTS = [
  { v: "featured", label: "Featured" },
  { v: "newest", label: "Newest" },
  { v: "price_asc", label: "Price · Low" },
  { v: "price_desc", label: "Price · High" },
];

const CAT_NAMES = {
  "beds": "Beds",
  "sofas-seating": "Sofas & Seating",
  "dining-sets": "Dining Sets",
  "chairs": "Chairs",
  "tables": "Tables",
  "wardrobes-storage": "Wardrobes & Storage",
  "tv-units": "TV Units & Cabinets",
  "bookshelves": "Bookshelves",
  "swatches": "Sample Swatches",
};

export default function Category() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState(sp.get("sort") || "featured");
  const [subCat, setSubCat] = useState(sp.get("sub") || "");
  const [wood, setWood] = useState(sp.get("wood") || "");
  const [priceMax, setPriceMax] = useState(sp.get("max_price") || "");

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => {
      setCategory(r.data.find((c) => c.slug === slug));
    });
  }, [slug]);

  // sync state -> URL (so shareable links preserve filters)
  useEffect(() => {
    const next = {};
    if (subCat) next.sub = subCat;
    if (wood) next.wood = wood;
    if (priceMax) next.max_price = priceMax;
    if (sort && sort !== "featured") next.sort = sort;
    setSp(next, { replace: true });
  }, [subCat, wood, priceMax, sort]);

  // reset filters when category slug changes (except honor initial URL params)
  useEffect(() => {
    setSubCat(sp.get("sub") || "");
    setWood(sp.get("wood") || "");
    setPriceMax(sp.get("max_price") || "");
    setSort(sp.get("sort") || "featured");
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    const params = { category: slug, sort };
    if (subCat) params.sub_category = subCat;
    if (wood) params.wood_finish = wood;
    if (priceMax) params.max_price = priceMax;
    axios.get(`${API}/products`, { params })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [slug, sort, subCat, wood, priceMax]);

  const woodOptions = ["Natural Teak", "Walnut", "Ebony", "Oak"];
  const showWoodFilter = slug !== "swatches";

  return (
    <div className="fade-in max-w-[1440px] mx-auto px-6 md:px-12 py-16" data-testid="category-page">
      <div className="mb-12">
        <div className="font-micro text-[#8A8680] mb-4">Collection</div>
        <h1 className="font-serif text-[#EDE4D3] text-5xl md:text-6xl">{CAT_NAMES[slug] || slug}</h1>
        {subCat && (
          <div className="mt-4 font-micro text-[#B8895C]" data-testid="category-active-sub">
            Filter: {subCat} · <button onClick={() => setSubCat("")} className="link-underline text-[#8A8680]" data-testid="category-clear-sub">Clear</button>
          </div>
        )}
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12 py-6" style={{ borderTop: "1px solid var(--hairline)", borderBottom: "1px solid var(--hairline)" }}>
        <div className="flex flex-wrap gap-3">
          {category?.sub_categories?.map((sc) => (
            <button key={sc} className={`chip ${subCat === sc ? "active" : ""}`} onClick={() => setSubCat(subCat === sc ? "" : sc)} data-testid={`filter-sub-${sc}`}>
              {sc}
            </button>
          ))}
          {showWoodFilter && woodOptions.map((w) => (
            <button key={w} className={`chip ${wood === w ? "active" : ""}`} onClick={() => setWood(wood === w ? "" : w)} data-testid={`filter-wood-${w}`}>
              {w}
            </button>
          ))}
          {showWoodFilter && (
            <select className="chip bg-transparent" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} data-testid="filter-price-max">
              <option value="">Max price</option>
              <option value="30000">Under ₹30k</option>
              <option value="60000">Under ₹60k</option>
              <option value="100000">Under ₹1L</option>
              <option value="150000">Under ₹1.5L</option>
            </select>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="font-micro text-[#8A8680]">Sort</div>
          <select className="chip bg-transparent" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="filter-sort">
            {SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-[#8A8680] font-micro py-24 text-center">Loading…</div>
      ) : products.length === 0 ? (
        <div className="text-[#8A8680] font-serif text-2xl py-24 text-center">No pieces match those filters yet.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-10">
          {products.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      )}
    </div>
  );
}
